/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Select, Table, Space, Modal } from "antd";
import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";

import CommentsModal from "./CommentsModal";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import {
  LectureFeedbackQuestionTable,
  LectureFeedbackTableBasicColumns
} from "../../common-utils/LectureFeedback";

const LectureFeedbackModal = ({
  isModalOpen = false,
  setIsModalOpen = () => {},
  batchList = [],
  currentEducatorId = "",
  ROOT_URL = "",
  coursesList = []
}) => {
  const currentDate = new Date();
  const twoMonthsAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 2,
    currentDate.getDate()
  );
  const [filter, setFilter] = useState({
    educatorId: currentEducatorId,
    startDate: twoMonthsAgo,
    endDate: currentDate
  });
  const [batch, setSelectedBatch] = useState("");
  const [course, setCourse] = useState("");
  const [courseList, setCourseList] = useState([
    { label: "ALL COURSES", value: "" },
    ...coursesList
  ]);
  const [isViewCommentModalOpen, setIsViewCommentModalOpen] = useState(false);
  const [lectureFeedbackComments, setLectureFeedbackComments] = useState([]);
  const [lecturesFeedbackColumns, setLecturesFeedbackColumns] = useState([]);
  const [lecturesFeedbacks, setLecturesFeedbacks] = useState([]);
  const [lectureFeedbackQuestionsList, setLectureFeedbackQuestionsList] = useState([]);
  const [educatorFeedbackCsvData, setEducatorFeedbackCsvData] = useState([]);
  const csvLinkRef = useRef(null);

  const filterCoursesForBatch = (batchId) => {
    const courseList = batchId.length
      ? coursesList.filter((course) => course.batchId === batchId)
      : coursesList;
    setCourseList([{ label: "ALL COURSES", value: "" }, ...courseList]);
  };

  const fetchEducatorLecturesFeedbacks = () => {
    axios
      .get(`${ROOT_URL}/admin/course-educator/lecture-feedbacks/list`, {
        params: {
          filter
        }
      })
      .then(({ data }) => {
        const { lecturesFeedbacks, questions } = data;
        setLecturesFeedbacks(lecturesFeedbacks);
        setLectureFeedbackQuestionsList(questions);
        showSuccessNotification("Lectures Feedbacks fetched successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchFeedbackCommentsOnLecture = (lectureId) => {
    axios
      .get(`${ROOT_URL}/admin/course-educator/lecture-feedbacks/comments`, {
        params: {
          filter: {
            lectureId,
            ...filter
          }
        }
      })
      .then(({ data }) => {
        const { lectureComments = [] } = data;
        setLectureFeedbackComments(lectureComments);
        showSuccessNotification("Comments fetched successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleClickFilter = () => {
    fetchEducatorLecturesFeedbacks();
  };

  const handleDownloadCsv = () => {
    axios
      .get(`${ROOT_URL}/admin/course-educator/lecture-feedbacks/download-csv`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { feedbacks, questions } = res?.data || {};
        setEducatorFeedbackCsvData(
          (feedbacks || []).map((feedback) => {
            const comments = feedback.comments.map((comment) => comment.comment).join("\n");

            return {
              "Course Name": feedback.courseDisplayName,
              "Lecture Name": feedback.lectureName,
              [questions[0].question]: feedback.question_1,
              [questions[1].question]: feedback.question_2,
              [questions[2].question]: feedback.question_3,
              "Total Responses": feedback.totalResponses,
              Comments: comments
            };
          })
        );
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  useEffect(() => {
    if (educatorFeedbackCsvData.length > 0 && csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  }, [educatorFeedbackCsvData]);

  const componentSpecificColumns = [
    {
      title: "More Options",
      key: "action",
      width: 200,

      render: (row) => (
        <div
          id="educator_feedback_row_actions"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around"
          }}
        >
          <Space wrap>
            <>
              <Button
                type="primary"
                onClick={() => {
                  setIsViewCommentModalOpen(true);
                  fetchFeedbackCommentsOnLecture(row.lectureId);
                }}
              >
                View Comments
              </Button>
            </>
          </Space>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchEducatorLecturesFeedbacks();
  }, [currentEducatorId]);

  useEffect(() => {
    setLecturesFeedbackColumns(LectureFeedbackTableBasicColumns().concat(componentSpecificColumns));
    setCourse(null);
    setFilter({ ...filter, courseId: "" });
    filterCoursesForBatch(batch);
  }, [batch]);

  return (
    <Modal
      title="Educator Analytics"
      centered
      width={2000}
      open={isModalOpen}
      footer={[]}
      onOk={() => {
        setIsModalOpen(false);
      }}
      onCancel={() => {
        setIsModalOpen(false);
      }}
    >
      <LectureFeedbackQuestionTable questions={lectureFeedbackQuestionsList} />
      <div
        className="filter-div"
        style={{
          display: "flex",
          flexDirection: "row",
          width: "94%",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "10px",
          flexWrap: "wrap"
        }}
      >
        <div
          className="select-batch-course"
          style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
        >
          <Select
            placeholder="Select Batch"
            options={batchList}
            onSelect={(value) => {
              setSelectedBatch(value);
              setFilter({ ...filter, batchId: value });
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => (option?.label ?? "").includes(input)}
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
          />
          <Select
            placeholder="Select Course"
            value={course}
            options={courseList}
            onSelect={(value) => {
              setCourse(value);
              setFilter({ ...filter, courseId: value });
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => (option?.label ?? "").includes(input)}
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
          />
        </div>
        <div className="submit-filter">
          <Button
            type="primary"
            onClick={() => {
              handleClickFilter();
            }}
          >
            Filter
          </Button>
          <CSVLink
            asyncOnClick={true}
            data={educatorFeedbackCsvData || []}
            ref={csvLinkRef}
            filename={"educator_feedback.csv"}
          />
          <Button
            disabled
            type="primary"
            onClick={() => {
              handleDownloadCsv();
            }}
          >
            Download CSV
          </Button>
        </div>
      </div>
      <Table
        columns={lecturesFeedbackColumns}
        dataSource={lecturesFeedbacks}
        bordered
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      {isViewCommentModalOpen === true && (
        <CommentsModal
          isModalOpen={isViewCommentModalOpen}
          setIsModalOpen={setIsViewCommentModalOpen}
          lectureFeedbackComments={lectureFeedbackComments}
        />
      )}
    </Modal>
  );
};

export default LectureFeedbackModal;
